"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { Loader2 } from "lucide-react"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useMobile } from "@/hooks/use-mobile"
import {
  creationFlowPrimaryCtaClass,
  creationFlowSectionLabelClass,
} from "@/lib/creation-flow-form-styles"
import { useToast } from "@/hooks/use-toast"
import { useOfferActions } from "@/hooks/use-offer"
import {
  OFFER_EXPIRY_PRESETS_HOURS,
  DEFAULT_OFFER_EXPIRY_HOURS,
  MAX_OFFER_MESSAGE_LENGTH,
} from "@/lib/offers/offer-types"
import { currencySymbol, generateClientOfferId } from "@/lib/offers/offer-utils"
import { cn } from "@/lib/utils"
import { OfferAutoDeclinedSheet } from "./offer-auto-declined-sheet"
import { OfferPendingBlockedSheet } from "./offer-pending-blocked-sheet"
import {
  OfferInfoBox,
  OfferPercentChip,
  OfferSavingsBadge,
  formatOfferPriceLabel,
  offerSheetTextareaClass,
} from "./offer-sheet-parts"
import { SavedListingPreviewCard } from "@/components/saved/saved-listing-preview-card"

const BUYER_DISCOUNT_PRESETS = [0.9, 0.8, 0.7] as const
const SELLER_DISCOUNT_PRESETS = [0.85, 0.9, 0.95] as const

type OfferModalMode =
  | { kind: "create"; listingId: string; listingPrice: number | null; currency: string | null }
  | {
      kind: "counter"
      parentOfferId: string
      listingPrice: number | null
      currency: string | null
      receivedOfferAmount: number
    }

interface OfferModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: OfferModalMode
  listingPreview?: {
    title: string
    imageUrl: string | null
    condition?: string | null
    grade?: string | number | null
  }
  sellerUsername?: string | null
  recipientUsername?: string | null
  onSuccess?: (offerId: string) => void
  zIndex?: number
}

export function OfferModal(props: OfferModalProps) {
  const isMobile = useMobile()
  const { open, onOpenChange, zIndex = 10100 } = props
  const t = useTranslations("offers.modal")
  const form = useOfferModalForm(props)

  const panelTitle =
    props.mode.kind === "create"
      ? t("createTitle", { default: "Make an offer" })
      : t("counterTitle", { default: "Send counter offer" })

  const listingCard =
    props.listingPreview && !form.success ? (
      <SavedListingPreviewCard
        title={props.listingPreview.title}
        imageUrl={props.listingPreview.imageUrl}
        price={props.mode.listingPrice}
        currency={props.mode.currency}
        condition={props.listingPreview.condition}
        grade={props.listingPreview.grade}
        tone="sheet"
        priceStrikethrough
      />
    ) : null

  if (isMobile) {
    return (
      <>
        <DockSheet
          open={open}
          onOpenChange={onOpenChange}
          title={panelTitle}
          zIndex={zIndex}
          belowTitle={listingCard}
          footer={form.success ? undefined : form.submitButton}
        >
          <div className="flex flex-col gap-4">{form.fields}</div>
        </DockSheet>
        {form.overlays}
      </>
    )
  }

  return (
    <>
      {/* Same props as the `DockSheet` above, on purpose: this pair is the
          reference case for the desktop/mobile chrome symmetry (HANDOFF §4). */}
      <DialogCard
        open={open}
        onOpenChange={onOpenChange}
        title={panelTitle}
        belowTitle={listingCard}
        panelClassName="h-[min(90dvh,720px)] max-h-[min(90dvh,720px)] max-w-md"
        footer={form.success ? undefined : form.submitButton}
      >
        <div className="flex flex-col gap-4">{form.fields}</div>
      </DialogCard>
      {form.overlays}
    </>
  )
}

function useOfferModalForm({
  open,
  onOpenChange,
  mode,
  sellerUsername,
  recipientUsername,
  onSuccess,
}: OfferModalProps) {
  const t = useTranslations()
  const { toast } = useToast()
  const actions = useOfferActions()

  const [amountStr, setAmountStr] = useState("")
  const [message, setMessage] = useState("")
  const [expiresInHours, setExpiresInHours] = useState<number>(DEFAULT_OFFER_EXPIRY_HOURS)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [pendingBlockedOpen, setPendingBlockedOpen] = useState(false)
  const [pendingBlockedOfferId, setPendingBlockedOfferId] = useState<string | null>(null)
  const [pendingResubmit, setPendingResubmit] = useState<{
    amount: number
    message: string | null
    expiresInHours: number
  } | null>(null)
  const [autoDeclinedOpen, setAutoDeclinedOpen] = useState(false)
  const clientOfferIdRef = useRef<string>(generateClientOfferId())

  const setPendingSheetOpen = (next: boolean) => {
    setPendingBlockedOpen(next)
    if (!next) setPendingResubmit(null)
  }
  const symbol = currencySymbol(mode.currency)
  const listPrice = mode.listingPrice

  const defaultAmountStr = useMemo(() => {
    if (mode.kind === "create") {
      if (listPrice != null && listPrice > 0) {
        return String(Math.round(listPrice * BUYER_DISCOUNT_PRESETS[0]))
      }
      return ""
    }
    if (listPrice != null && listPrice > 0) {
      return String(Math.round(listPrice * SELLER_DISCOUNT_PRESETS[2]))
    }
    return String(Math.round(mode.receivedOfferAmount))
  }, [mode, listPrice])

  const expiryOptions = useMemo(
    () =>
      OFFER_EXPIRY_PRESETS_HOURS.map((h) => ({
        value: String(h),
        label: formatExpiryLabel(h, t),
      })),
    [t],
  )

  useEffect(() => {
    if (!open) return
    setAmountStr(defaultAmountStr)
    setMessage("")
    setExpiresInHours(DEFAULT_OFFER_EXPIRY_HOURS)
    setError(null)
    setSuccess(false)
    setPendingBlockedOpen(false)
    setPendingBlockedOfferId(null)
    setPendingResubmit(null)
    setAutoDeclinedOpen(false)
    clientOfferIdRef.current = generateClientOfferId()
  }, [open, mode.kind, defaultAmountStr])

  const parsedAmount = useMemo(() => {
    if (amountStr.trim() === "") return null
    const n = Number(amountStr.replace(",", "."))
    return Number.isFinite(n) && n > 0 ? n : null
  }, [amountStr])

  const discount =
    listPrice != null && parsedAmount != null && listPrice > parsedAmount
      ? Math.round((1 - parsedAmount / listPrice) * 100)
      : null
  const savings =
    listPrice != null && parsedAmount != null && listPrice > parsedAmount
      ? listPrice - parsedAmount
      : null

  const discountPresets =
    mode.kind === "create" ? BUYER_DISCOUNT_PRESETS : SELLER_DISCOUNT_PRESETS

  const percentageOptions = useMemo(() => {
    if (listPrice == null || listPrice <= 0) return []
    return discountPresets.map((ratio) => {
      const value = Math.round(listPrice * ratio)
      const pct = Math.round((1 - ratio) * 100)
      return {
        label: `-${pct}%`,
        value,
        amountLabel: formatOfferPriceLabel(value, mode.currency),
      }
    })
  }, [listPrice, mode.currency, discountPresets])

  const isOverPrice = parsedAmount != null && listPrice != null && parsedAmount >= listPrice
  const isCounterTooLow =
    mode.kind === "counter" &&
    parsedAmount != null &&
    parsedAmount <= mode.receivedOfferAmount
  const canSubmit =
    parsedAmount != null && !submitting && !isOverPrice && !isCounterTooLow

  const handleSubmit = async () => {
    if (!parsedAmount || isOverPrice || isCounterTooLow) return
    setSubmitting(true)
    setError(null)
    try {
      const result =
        mode.kind === "create"
          ? await actions.placeOffer({
              listingId: mode.listingId,
              amount: parsedAmount,
              message: message.trim() || null,
              expiresInHours,
              clientOfferId: clientOfferIdRef.current,
            })
          : await actions.counterOffer({
              parentOfferId: mode.parentOfferId,
              amount: parsedAmount,
              message: message.trim() || null,
              expiresInHours,
              clientOfferId: clientOfferIdRef.current,
            })

      if (!result.ok) {
        const data = result.data as { reason?: string; error?: string; existing_offer_id?: string } | null
        const reason = data?.reason ?? data?.error ?? "unknown_error"
        if (mode.kind === "create" && reason === "already_pending_offer") {
          setPendingResubmit({
            amount: parsedAmount,
            message: message.trim() || null,
            expiresInHours,
          })
          setPendingBlockedOfferId(
            typeof data?.existing_offer_id === "string" ? data.existing_offer_id : null,
          )
          setPendingSheetOpen(true)
          return
        }
        setError(humanizeReason(reason, t))
        clientOfferIdRef.current = generateClientOfferId()
        return
      }

      const data = result.data as { offer?: { id: string }; auto_declined?: boolean }
      const offerId = data?.offer?.id

      if (data?.auto_declined) {
        setAutoDeclinedOpen(true)
        clientOfferIdRef.current = generateClientOfferId()
        return
      }

      setSuccess(true)
      toast({
        title:
          mode.kind === "create"
            ? t("offers.toasts.sent.title", { default: "Offer sent" })
            : t("offers.toasts.counterSent.title", { default: "Counter offer sent" }),
      })
      if (offerId) onSuccess?.(offerId)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error")
      clientOfferIdRef.current = generateClientOfferId()
    } finally {
      setSubmitting(false)
    }
  }

  const submitLabel =
    parsedAmount != null
      ? mode.kind === "create"
        ? t("offers.modal.submitCreateAmount", {
            default: "Send offer · {amount}",
            amount: formatOfferPriceLabel(parsedAmount, mode.currency),
          })
        : t("offers.modal.submitCounterAmount", {
            default: "Send counter · {amount}",
            amount: formatOfferPriceLabel(parsedAmount, mode.currency),
          })
      : mode.kind === "create"
        ? t("offers.modal.submitCreate", { default: "Send offer" })
        : t("offers.modal.submitCounter", { default: "Send counter" })

  const submitButton = (
    <Button
      disabled={!canSubmit}
      onClick={handleSubmit}
      className={creationFlowPrimaryCtaClass}
    >
      {submitting ? (
        <span className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("offers.modal.sending", { default: "Sending..." })}
        </span>
      ) : (
        submitLabel
      )}
    </Button>
  )

  const messageRecipient =
    mode.kind === "create"
      ? sellerUsername
        ? sellerUsername.startsWith("@")
          ? sellerUsername
          : `@${sellerUsername}`
        : null
      : recipientUsername
        ? recipientUsername.startsWith("@")
          ? recipientUsername
          : `@${recipientUsername}`
        : null

  const amountEyebrow =
    mode.kind === "create"
      ? t("offers.modal.amountEyebrow", { default: "Your offer" })
      : t("offers.modal.counterEyebrow", { default: "Your counter-offer" })

  const fields = success ? (
    <div className="pt-2">
      <h3 className="text-lg font-bold text-foreground">
        {mode.kind === "create"
          ? t("offers.modal.successTitle", { default: "Offer sent" })
          : t("offers.modal.counterSuccessTitle", { default: "Counter offer sent" })}
      </h3>
      <p className="mt-2 text-sm leading-snug text-muted-foreground">
        {mode.kind === "create"
          ? t("offers.modal.successBody", {
              default: "The seller will be notified. You can track your offer in the Offers tab.",
            })
          : t("offers.modal.counterSuccessBody", {
              default: "The buyer will be notified and can accept, decline, or counter.",
            })}
      </p>
      <Button
        onClick={() => onOpenChange(false)}
        className={cn(creationFlowPrimaryCtaClass, "mt-4 w-full")}
      >
        {t("offers.modal.done", { default: "Done" })}
      </Button>
    </div>
  ) : (
    <>
      <div className="flex flex-col gap-3">
        <p className="text-center text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {amountEyebrow}
        </p>
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-xl font-semibold text-muted-foreground">{symbol}</span>
          <input
            inputMode="decimal"
            value={amountStr}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9.,]/g, "")
              if (v === "" || /^\d*[.,]?\d*$/.test(v)) setAmountStr(v)
            }}
            placeholder="0"
            aria-label={t("offers.modal.amountLabel", { default: "Your offer" })}
            className="min-w-[120px] border-0 bg-transparent p-0 text-center text-5xl font-extrabold text-foreground placeholder-muted-foreground/50 focus:outline-none focus:ring-0"
          />
        </div>

        {savings != null && discount != null ? (
          <OfferSavingsBadge
            savingsLabel={t("offers.modal.savings", {
              default: "Save {amount} (-{pct}%)",
              amount: formatOfferPriceLabel(savings, mode.currency),
              pct: discount,
            })}
          />
        ) : null}

        {percentageOptions.length > 0 ? (
          <div className="mt-1 flex flex-col gap-2">
            <p className="text-sm font-medium text-muted-foreground">
              {t("offers.modal.suggestions", { default: "Suggestions" })}
            </p>
            <div className="flex gap-3">
              {percentageOptions.map((opt) => (
                <OfferPercentChip
                  key={opt.label}
                  label={opt.label}
                  amountLabel={opt.amountLabel}
                  selected={parsedAmount === opt.value}
                  onPress={() => setAmountStr(String(opt.value))}
                />
              ))}
            </div>
          </div>
        ) : null}

        {isOverPrice ? (
          <p className="text-center text-xs text-amber-400">
            {t("offers.modal.overPriceWarning", {
              default: "Your offer is at or above asking price — consider buying directly.",
            })}
          </p>
        ) : null}

        {isCounterTooLow ? (
          <p className="text-center text-xs text-amber-400">
            {t("offers.modal.counterTooLow", {
              default: "Your counter must be higher than the buyer's offer.",
            })}
          </p>
        ) : null}
      </div>

      <label className="block">
        <span className="mb-2 block text-sm font-bold text-foreground">
          {messageRecipient
            ? t("offers.modal.messageTo", {
                default: "Message to {user}",
                user: messageRecipient,
              })
            : t("offers.modal.messageLabel", { default: "Message (optional)" })}
          <span className="font-normal text-muted-foreground">
            {" "}
            · {t("offers.modal.optionalSuffix", { default: "optional" })}
          </span>
        </span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_OFFER_MESSAGE_LENGTH))}
          rows={3}
          placeholder={
            mode.kind === "counter"
              ? t("offers.modal.counterMessagePlaceholder", {
                  default: "Explain your counter-offer…",
                })
              : t("offers.modal.messagePlaceholder", {
                  default: "Add a note to the seller...",
                })
          }
          className={offerSheetTextareaClass}
        />
        <p className="mt-1 text-right text-[10px] text-muted-foreground/70">
          {message.length}/{MAX_OFFER_MESSAGE_LENGTH}
        </p>
      </label>

      <div className="block">
        <span className={cn(creationFlowSectionLabelClass, "mb-1.5 block")}>
          {t("offers.modal.expiryLabel", { default: "Expires in" })}
        </span>
        <Select
          surface="dialog"
          value={String(expiresInHours)}
          onValueChange={(value) => setExpiresInHours(Number(value))}
        >
          <SelectTrigger className="h-12 rounded-3xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {expiryOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <OfferInfoBox>
        {mode.kind === "counter" && listPrice != null && parsedAmount != null ? (
          t("offers.modal.counterRange", {
            default: "Between {low} (offer) and {high} (price)",
            low: formatOfferPriceLabel(mode.receivedOfferAmount, mode.currency),
            high: formatOfferPriceLabel(listPrice, mode.currency),
          })
        ) : (
          t("offers.modal.disclaimer", {
            default:
              "Offers are binding once accepted. The seller can accept, decline, or send a counter-offer.",
          })
        )}
      </OfferInfoBox>

      {error ? (
        <div className="rounded-xl border border-red-800/40 bg-red-900/40 p-3 text-sm text-red-200">
          {error}
        </div>
      ) : null}
    </>
  )

  const overlays = (
    <>
      {mode.kind === "create" ? (
        <OfferPendingBlockedSheet
          open={pendingBlockedOpen}
          onOpenChange={setPendingSheetOpen}
          listingId={mode.listingId}
          initialOfferId={pendingBlockedOfferId}
          onWithdrawn={() => {
            setError(null)
            clientOfferIdRef.current = generateClientOfferId()
          }}
          resubmitCreateOffer={
            pendingResubmit
              ? {
                  amount: pendingResubmit.amount,
                  message: pendingResubmit.message,
                  expiresInHours: pendingResubmit.expiresInHours,
                  currency: mode.currency,
                }
              : null
          }
          onResubmitSuccess={(offerId) => {
            toast({
              title: t("offers.toasts.sent.title", { default: "Offer sent" }),
            })
            setSuccess(true)
            onSuccess?.(offerId)
            clientOfferIdRef.current = generateClientOfferId()
            setPendingResubmit(null)
          }}
        />
      ) : null}
      <OfferAutoDeclinedSheet open={autoDeclinedOpen} onOpenChange={setAutoDeclinedOpen} />
    </>
  )

  return { fields, submitButton, overlays, success }
}

function formatExpiryLabel(hours: number, t: ReturnType<typeof useTranslations>): string {
  if (hours < 24) return t("offers.expiry.hours", { default: `${hours} hour${hours > 1 ? "s" : ""}`, hours })
  if (hours === 24) return t("offers.expiry.day", { default: "1 day" })
  const days = Math.round(hours / 24)
  return t("offers.expiry.days", { default: `${days} days`, days })
}

function humanizeReason(reason: string, t: ReturnType<typeof useTranslations>): string {
  switch (reason) {
    case "already_pending_offer":
      return t("offers.errors.alreadyPending", {
        default: "You already have a pending offer on this listing — withdraw it first.",
      })
    case "cannot_offer_own_listing":
      return t("offers.errors.ownListing", { default: "You cannot make an offer on your own listing." })
    case "listing_does_not_accept_offers":
      return t("offers.errors.notAccepting", { default: "This listing does not accept offers." })
    case "listing_not_found":
    case "listing_deleted":
      return t("offers.errors.listingUnavailable", { default: "This listing is no longer available." })
    case "buyer_banned":
      return t("offers.errors.buyerBanned", { default: "Your account is restricted." })
    case "seller_banned":
      return t("offers.errors.sellerBanned", { default: "The seller's account is unavailable." })
    case "max_counter_depth_reached":
      return t("offers.errors.maxCounterDepth", {
        default: "Maximum negotiation depth reached. Please accept, decline, or send a fresh offer.",
      })
    case "parent_expired":
      return t("offers.errors.parentExpired", { default: "The previous offer has expired." })
    case "invalid_amount":
      return t("offers.errors.invalidAmount", { default: "Please enter a valid amount." })
    case "invalid_expires_in_hours":
      return t("offers.errors.invalidExpiry", { default: "Pick a valid expiration window." })
    case "message_too_long":
      return t("offers.errors.messageTooLong", {
        default: "Your message is too long. Please shorten it.",
      })
    case "unauthorized":
      return t("offers.errors.unauthorized", {
        default: "Please log in again to send this offer.",
      })
    case "below_minimum":
      return t("offers.errors.belowMinimumInline", {
        default: "Your offer is below the seller's minimum and was auto-declined. Try a higher amount.",
      })
    default:
      return t("offers.errors.generic", { default: "Could not send your offer. Please try again." })
  }
}
